import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { NavBar, Card, Button, Toast, SpinLoading } from 'antd-mobile';
import { parseDeviceInfoFromUrl } from '../../utils/urlParams';
import { getPortalConfig } from '../../services/portalApi';
import { DeviceInfo, PortalConfig } from '../../types/portal';
import './index.css';

const Portal: React.FC = () => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [config, setConfig] = useState<PortalConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // 1. 从URL解析设备信息
    const info = parseDeviceInfoFromUrl();
    
    if (!info) {
      setError('缺少设备信息参数，请使用正确的二维码访问');
      setLoading(false);
      return;
    }

    setDeviceInfo(info);

    // 2. 获取后台配置（后台会生成带设备信息的服务号二维码）
    getPortalConfig(info)
      .then((portalConfig) => {
        setConfig(portalConfig);
        setLoading(false);
      })
      .catch((err) => {
        console.error('获取配置失败:', err);
        setError('获取配置失败，请稍后重试');
        setLoading(false);
      });
  }, []);


  // 复制设备信息链接（用于调试或分享）
  const handleCopyDeviceInfo = () => {
    if (!deviceInfo) return;

    const currentUrl = window.location.href;
    navigator.clipboard
      .writeText(currentUrl)
      .then(() => {
        Toast.show({
          content: '链接已复制',
          position: 'top',
        });
      })
      .catch(() => {
        Toast.show({
          content: '复制失败',
          position: 'top',
        });
      });
  };

  if (loading) {
    return (
      <div className="portal-container">
        <div className="portal-loading">
          <SpinLoading style={{ '--size': '48px' }} />
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !deviceInfo || !config) {
    return (
      <div className="portal-container">
        <NavBar back={null}>设备激活</NavBar>
        <div className="portal-error">
          <p>{error || '配置加载失败'}</p>
          <p className="portal-error-tip">
            请确保使用正确的二维码访问，或联系客服获取帮助
          </p>
        </div>
      </div>
    );
  }

  const isWechat = config.type === 'wechat';
  const qrCodeUrl = isWechat ? config.wechatQrCodeUrl : config.workWechatQrCodeUrl;
  const title = isWechat ? '关注服务号' : '关注企业微信';
  const description = isWechat
    ? '请长按识别下方二维码关注服务号，然后进入小程序完成设备绑定'
    : '请长按识别下方二维码关注企业微信，然后进入小程序完成设备绑定';

  return (
    <div className="portal-container">
      <NavBar back={null}>设备激活</NavBar>
      
      <div className="portal-content">
        <Card title={title}>
          <div className="portal-info">
            <p className="portal-description">{description}</p>
            
            {/* 设备信息展示 */}
            <div className="portal-device-info">
              <p className="device-info-label">设备信息：</p>
              <p className="device-info-value">
                类型：{deviceInfo.deviceType === 'FULL_DUPLEX' ? '全双工' : '半双工'}
              </p>
              {deviceInfo.version && (
                <p className="device-info-value">版本：{deviceInfo.version}</p>
              )}
            </div>

            {/* 二维码展示 */}
            {qrCodeUrl && (
              <div className="portal-qrcode">
                <QRCodeSVG
                  value={qrCodeUrl}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
                <p className="qrcode-tip">长按识别二维码</p>
              </div>
            )}

            {/* 说明文字 */}
            <div className="portal-tips">
              <p className="tip-title">温馨提示：</p>
              <ul className="tip-list">
                <li>设备信息已自动记录在二维码中</li>
                <li>关注服务号后，系统会自动识别您的设备类型</li>
                <li>服务号会为您推送带设备信息的小程序链接</li>
                <li>点击链接进入小程序即可完成设备绑定</li>
                <li>如遇问题，请联系客服</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* 调试信息（开发环境显示） */}
        {process.env.NODE_ENV === 'development' && (
          <Card title="调试信息" style={{ marginTop: '16px' }}>
            <div className="portal-debug">
              <p>
                <strong>设备信息：</strong>
                {JSON.stringify(deviceInfo, null, 2)}
              </p>
              <p>
                <strong>配置类型：</strong>
                {config.type}
              </p>
              <Button
                size="small"
                onClick={handleCopyDeviceInfo}
                style={{ marginTop: '8px' }}
              >
                复制当前链接
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Portal;

